// src/pages/fullServiceKm/hooks/useFullServiceKm.js
import { useState, useMemo } from 'react';
import { useGetFullServiceKmQuery, useCreateFullServiceKmMutation, useUpdateFullServiceKmMutation, useDeleteFullServiceKmMutation, useChangeFullServiceKmStatusMutation } from '../../../../api/endpoints/fullServiceKm';

export const FullServiceKmStatus = {
    Draft: 10,
    WaitingApproval: 20,
    Returned: 25,
    Rejected: 30,
    Accepted: 40
};

export const STATUS_META = {
    [FullServiceKmStatus.Draft]: {
        label: 'Taslak',
        severity: 'info',
        group: 'Genel'
    },
    [FullServiceKmStatus.WaitingApproval]: {
        label: 'Onay Bekliyor',
        severity: 'warning',
        group: 'Onay'
    },
    [FullServiceKmStatus.Returned]: {
        label: 'İade',
        severity: 'info',
        group: 'Onay'
    },
    [FullServiceKmStatus.Rejected]: {
        label: 'Reddedildi',
        severity: 'danger',
        group: 'Onay'
    },
    [FullServiceKmStatus.Accepted]: {
        label: 'Onaylandı',
        severity: 'success',
        group: 'Onay'
    }
};

const useFullServiceKm = (fullServiceId) => {
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 1,
        sortField: null,
        sortOrder: null
    });

    const queryEnabled = Boolean(fullServiceId);

    const { data, isLoading, refetch } = useGetFullServiceKmQuery(
        {
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            fullServiceId: fullServiceId
        },
        { skip: !queryEnabled }
    );

    const [createFullServiceKm] = useCreateFullServiceKmMutation();
    const [updateFullServiceKm, { isLoading: isUpdatingKm }] = useUpdateFullServiceKmMutation();
    const [deleteFullServiceKm] = useDeleteFullServiceKmMutation();

    const sortedData = useMemo(() => {
        const items = data?.data?.items || [];
        if (!lazyParams.sortField) return items;

        return [...items].sort((a, b) => {
            const aVal = a[lazyParams.sortField] || '';
            const bVal = b[lazyParams.sortField] || '';
            const order = lazyParams.sortOrder === 1 ? 1 : -1;
            return order * aVal.toString().localeCompare(bVal.toString(), 'tr', { numeric: true });
        });
    }, [data, lazyParams.sortField, lazyParams.sortOrder]);

    const onPage = (event) => {
        setLazyParams({
            first: event.first,
            rows: event.rows,
            page: event.page + 1
        });
    };

    const onSort = (event) => {
        setLazyParams((prev) => ({
            ...prev,
            sortField: event.sortField,
            sortOrder: event.sortOrder
        }));
    };

    const handleCreateKm = async (formData) => {
        try {
            const response = await createFullServiceKm(formData).unwrap();
            refetch();
            return response;
        } catch (error) {
            console.error('Error creating KM record:', error);
            return false;
        }
    };

    const handleUpdateKm = async (formData) => {
        try {
            const response = await updateFullServiceKm(formData).unwrap();
            refetch();
            return response;
        } catch (error) {
            console.error('Error updating KM record:', error);
            return false;
        }
    };

    const handleDeleteKm = async (id) => {
        try {
            const response = await deleteFullServiceKm(id).unwrap();
            refetch();
            return response;
        } catch (error) {
            console.error('Error deleting KM record:', error);
            return false;
        }
    };

    const [changeStatus, { isLoading: isChangingStatus }] = useChangeFullServiceKmStatusMutation();

    const handleChangeStatus = async ({ id, nextStatus, reason }) => {
        try {
            const response = await changeStatus({
                id,
                newStatus: nextStatus,
                note: reason || ''
            }).unwrap();

            refetch();
            return response;
        } catch (error) {
            console.error('Error changing KM status:', error);
            return false;
        }
    };

    return {
        kms: sortedData,
        originalData: data?.data?.items || [],
        totalCount: data?.data?.totalCount || 0,
        loading: isLoading,
        lazyParams,
        onPage,
        onSort,
        handleCreateKm,
        handleUpdateKm,
        handleDeleteKm,
        handleChangeStatus,
        updatingKm: isUpdatingKm,
        changingStatus: isChangingStatus,
        refetch
    };
};

export default useFullServiceKm;
